import React, {
  createContext,
  useContext,
  useState,
  useEffect,
  useCallback,
  useMemo,
  useRef,
} from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/hooks/useAuth';
import { useInterviewerAccess } from '@/hooks/useInterviewerAccess';
import { toast } from 'sonner';
import { Company } from '@/types/company';
interface CompanyContextType {
  companies: Company[];
  selectedCompany: Company | null;
  setSelectedCompany: (company: Company | null) => void;
  loading: boolean;
  error: Error | null;
  refetch: () => void;
}

const CompanyContext = createContext<CompanyContextType | undefined>(undefined);

export const useCompany = () => {
  const context = useContext(CompanyContext);
  if (!context) {
    throw new Error('useCompany must be used within a CompanyProvider');
  }
  return context;
};

export const CompanyProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [companies, setCompanies] = useState<Company[]>([]);
  const [selectedCompany, setSelectedCompany] = useState<Company | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);
  const {
    tenantId,
    isTenantInterviewer,
    isTenantAdmin,
    isSystemAdmin,
    session,
    isLoading: authLoading,
  } = useAuth();
  const { accessibleCompanies, isLoading: accessLoading } = useInterviewerAccess();
  const timeoutRef = useRef<NodeJS.Timeout | null>(null);
  const lastFetchRef = useRef<string>('');
  const previousCompanyCountRef = useRef<number>(0);

  // Save selected company to localStorage when it changes
  useEffect(() => {
    if (selectedCompany) {
      localStorage.setItem('selectedCompanyId', selectedCompany.id);
    }
  }, [selectedCompany]);

  const fetchCompanies = useCallback(async () => {
    // System admins don't need companies, exit early to prevent unnecessary processing
    if (isSystemAdmin) {
      console.log('[CompanyContext] System admin detected, skipping company fetch');
      setCompanies([]);
      setSelectedCompany(null);
      setLoading(false);
      return;
    }

    console.log('[CompanyContext] fetchCompanies called:', {
      authLoading,
      tenantId,
      hasSession: !!session?.access_token,
      isTenantInterviewer,
      accessLoading,
    });

    // If auth is still loading, wait
    if (authLoading) {
      console.log('[CompanyContext] Auth still loading, skipping fetch');
      return;
    }

    // If auth is ready but no tenant/session, that's expected for system admins
    if (!tenantId || !session?.access_token) {
      console.log('[CompanyContext] No tenantId or session, setting empty state');
      setCompanies([]);
      setSelectedCompany(null);
      setLoading(false);
      return;
    }

    // For tenant interviewers, wait for accessible companies to load
    if (isTenantInterviewer && accessLoading) {
      console.log('[CompanyContext] Waiting for interviewer access to load');
      return;
    }

    // Create a unique key for this fetch attempt
    const fetchKey = `${tenantId}-${isTenantInterviewer}-${session?.access_token?.substring(0, 10)}`;

    // Skip if this is the same request we just made
    if (lastFetchRef.current === fetchKey) {
      console.log('[CompanyContext] Skipping duplicate fetch request');
      return;
    }

    console.log('[CompanyContext] Starting fetch with key:', fetchKey);

    try {
      setLoading(true);
      lastFetchRef.current = fetchKey;

      console.log('[CompanyContext] About to execute database query');

      const query = supabase
        .from('companies')
        .select(
          `
          *,
          positions(count),
          company_mapping(handle)
        `
        )
        .eq('tenant_id', tenantId)
        .neq('status', 'archived')
        .order('name');

      // Note: Company access filtering for interviewers is now handled
      // in components that specifically need it, not in this shared context

      console.log('[CompanyContext] Executing query for tenantId:', tenantId);
      const { data, error } = await query;

      console.log('[CompanyContext] Query completed:', { dataLength: data?.length, error });

      if (error) {
        console.error('CompanyContext: Error fetching companies:', error);
        throw error;
      }

      // Transform data to ensure compatibility with Company type
      let transformedData = (data || []).map(company => ({
        ...company,
        about: company.about || null,
        mission: company.mission || null,
        vision: company.vision || null,
        benefits_data: company.benefits_data || { description: '', items: [] },
        values_data: company.values_data || { description: '', items: [] },
        positions: company.positions || [],
        handle: company.company_mapping?.[0]?.handle || undefined,
      })) as Company[];

      // Filter companies for tenant interviewers based on their access
      if (isTenantInterviewer && accessibleCompanies && accessibleCompanies.length > 0) {
        const accessibleCompanyIds = accessibleCompanies.map(ac => ac.company_id);
        transformedData = transformedData.filter(company =>
          accessibleCompanyIds.includes(company.id)
        );
        console.log(
          '[CompanyContext] Filtered companies for interviewer:',
          transformedData.map(c => c.name),
          'from accessible:',
          accessibleCompanyIds
        );
      }

      console.log(
        '[CompanyContext] Setting companies data:',
        transformedData.map(c => c.name)
      );
      setCompanies(transformedData);

      // Auto-select logic - prioritize newly created company
      if (transformedData && transformedData.length > 0) {
        setSelectedCompany(currentSelected => {
          // Check if we have more companies than before (indicates new company created)
          const previousCompanyCount = previousCompanyCountRef.current;
          const currentCompanyCount = transformedData.length;

          console.log('[CompanyContext] Auto-selection logic:', {
            previousCompanyCount,
            currentCompanyCount,
            currentSelected: currentSelected?.name,
            totalCompanies: transformedData.map(c => c.name),
          });

          // If we have a new company (count increased), select the most recently created one
          if (currentCompanyCount > previousCompanyCount) {
            console.log('[CompanyContext] New company detected! Selecting newest.');
            // Sort by created_at descending and take the newest
            const newestCompany = [...transformedData].sort(
              (a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime()
            )[0];
            console.log('[CompanyContext] Selected newest company:', newestCompany.name);
            return newestCompany;
          }

          // If already have a selection and no new companies, keep it
          if (currentSelected) {
            // Verify the current selection still exists in the new data
            const stillExists = transformedData.find(c => c.id === currentSelected.id);
            if (stillExists) {
              return currentSelected;
            }
          }

          // Try to restore from localStorage
          const savedCompanyId = localStorage.getItem('selectedCompanyId');
          if (savedCompanyId) {
            console.log('[CompanyContext] Trying localStorage company:', savedCompanyId);
            const found = transformedData.find(c => c.id === savedCompanyId);
            if (found) {
              console.log('[CompanyContext] Restored from localStorage:', found.name);
              return found;
            } else {
              console.log('[CompanyContext] localStorage company not found in current data');
            }
          }

          // For initial load with companies but no selection, select the most recent
          // This handles the case where user just completed company wizard and we're loading for first time
          if (!currentSelected && transformedData.length > 0) {
            console.log('[CompanyContext] Initial load - no selection, picking newest company');
            const newestCompany = [...transformedData].sort(
              (a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime()
            )[0];
            console.log('[CompanyContext] Initial load selected:', newestCompany.name);
            return newestCompany;
          }

          // NO FALLBACK - force explicit company selection
          console.log(
            '[CompanyContext] No company selection logic matched - returning null to force explicit selection'
          );
          return null;
        });

        // Update the previous company count for next comparison
        previousCompanyCountRef.current = transformedData.length;
      }
    } catch (err) {
      setError(err as Error);
      console.error('Error fetching companies:', err);
      toast.error('Failed to load companies');
    } finally {
      setLoading(false);
    }
  }, [
    isSystemAdmin,
    tenantId,
    session?.access_token,
    authLoading,
    isTenantInterviewer,
    accessibleCompanies,
  ]);

  useEffect(() => {
    // Don't even attempt to fetch for system admins
    if (isSystemAdmin) {
      setCompanies([]);
      setSelectedCompany(null);
      setLoading(false);
      return;
    }

    // Clear any existing timeout
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }

    // Debounce the fetch to avoid rapid successive calls
    timeoutRef.current = setTimeout(() => {
      fetchCompanies();
    }, 100);

    // Cleanup timeout on unmount
    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    };
  }, [isSystemAdmin, fetchCompanies]);

  // Note: Removed interviewer access coordination - let components handle filtering as needed

  // Custom setter that prevents clearing selection for tenant admins
  const handleSetSelectedCompany = useCallback(
    (company: Company | null) => {
      // For tenant admins, don't allow clearing the selection if companies exist
      if (isTenantAdmin && !company && companies.length > 0) {
        // Keep the current selection or select the first company
        setSelectedCompany(currentSelected => {
          return currentSelected || companies[0];
        });
        return;
      }
      setSelectedCompany(company);
    },
    [isTenantAdmin, companies]
  );

  // Force refetch function that bypasses duplicate protection
  const forceRefetch = useCallback(async () => {
    console.log('[CompanyContext] Force refetch requested - clearing lastFetchRef');
    lastFetchRef.current = ''; // Clear the ref to force refetch
    await fetchCompanies();
  }, [fetchCompanies]);

  const value = useMemo(
    () => ({
      companies,
      selectedCompany,
      setSelectedCompany: handleSetSelectedCompany,
      loading,
      error,
      refetch: forceRefetch,
    }),
    [companies, selectedCompany, handleSetSelectedCompany, loading, error, forceRefetch]
  );

  return <CompanyContext.Provider value={value}>{children}</CompanyContext.Provider>;
};
